import { AxiosError, AxiosRequestConfig } from 'axios'
import React from 'react'
import {
  QueryClient,
  QueryFunction,
  useInfiniteQuery,
  useQuery
} from 'react-query'
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult
} from 'react-query/types/react/types'
import { useDeepCompareEffect } from 'react-use'

import agent from 'agent'
import { PagedResponse } from 'types/api/services/dependencies/dependency'

import useAuth from './use-auth'

export const gremlinQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
})

// List of endpoints we don't want to add `?teamId=xxxx` to
// Can be string or Regex
const excludeTeamIdList: Array<string | RegExp> = [
  'teamId=',
  'providers/aws',
  'apikeys',
  'users/auth/mfa/info',
  'companies/',
  /teams\/(.*)\/profile/
]

export const getEndpointWithTeamId = (
  endpoint: string,
  currentTeamId: string
) =>
  excludeTeamIdList.some((test) =>
    test instanceof RegExp ? endpoint.match(test) : endpoint.includes(test)
  )
    ? endpoint
    : endpoint.includes('?')
    ? `${endpoint}&teamId=${currentTeamId}`
    : `${endpoint}?teamId=${currentTeamId}`

export async function fetchQueryData<TQueryFnData>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig
): Promise<TQueryFnData> {
  const res = await agent.get<TQueryFnData>(endpoint, axiosConfig)
  return res.data
}

export async function prefetchQuery<TQueryFnData>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig
): Promise<void> {
  const fetch = async () =>
    await fetchQueryData<TQueryFnData>(endpoint, axiosConfig)

  gremlinQueryClient.prefetchQuery(endpoint, fetch)
}

export function usePrefetchQueryData(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {}
): void {
  useDeepCompareEffect(() => {
    prefetchQuery(endpoint, axiosConfig)
  }, [endpoint, axiosConfig])
}

export function useGremlinQuery<TQueryFnData, TData = TQueryFnData>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {},
  useQueryOptions: UseQueryOptions<TQueryFnData, AxiosError, TData> = {}
): UseQueryResult<TData, AxiosError> {
  const { currentTeamId } = useAuth()

  const endpointWithTeamId = getEndpointWithTeamId(endpoint, currentTeamId)

  const fetchData = React.useCallback(
    async () =>
      await fetchQueryData<TQueryFnData>(endpointWithTeamId, axiosConfig),
    [endpointWithTeamId, axiosConfig]
  )

  // force enabled to be a boolean
  // otherwise `undefined` and other falsy values won't disable the query
  // https://github.com/TanStack/query/issues/2269
  if ('enabled' in useQueryOptions) {
    useQueryOptions.enabled = Boolean(useQueryOptions.enabled)
  }

  const fetchState = useQuery<TQueryFnData, AxiosError, TData>(
    endpointWithTeamId,
    fetchData,
    useQueryOptions
  )

  return fetchState
}

export async function prefetchPaginationData<
  TQueryFnData extends PagedResponse
>(endpoint: string, axiosConfig: AxiosRequestConfig): Promise<void> {
  const fetch = async () =>
    await fetchQueryData<TQueryFnData>(endpoint, axiosConfig)

  gremlinQueryClient.prefetchInfiniteQuery(endpoint, fetch)
}

export function usePrefetchPagintationData(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {}
): void {
  useDeepCompareEffect(() => {
    prefetchPaginationData(endpoint, axiosConfig)
  }, [endpoint, axiosConfig])
}

export function useGremlinPagination<
  TQueryFnData extends PagedResponse,
  TData = TQueryFnData
>(
  endpoint: string,
  axiosConfig: AxiosRequestConfig = {},
  options: UseInfiniteQueryOptions<TQueryFnData, AxiosError, TData> = {}
): UseInfiniteQueryResult<TData, AxiosError> {
  const { currentTeamId } = useAuth()
  const endpointWithTeamId = getEndpointWithTeamId(endpoint, currentTeamId)

  const fetchData = React.useCallback(
    async ({ pageParam = '' }) => {
      const queryParams = Object.assign(
        {},
        axiosConfig.params,
        pageParam && {
          pageToken: pageParam
        }
      )
      return await fetchQueryData(endpointWithTeamId, {
        ...axiosConfig,
        params: queryParams
      })
    },
    [endpointWithTeamId, axiosConfig]
  ) as QueryFunction<TQueryFnData>

  const fetchState = useInfiniteQuery<TQueryFnData, AxiosError, TData>(
    endpointWithTeamId,
    fetchData,
    {
      getNextPageParam: (lastPage) => lastPage.pageToken,
      ...options
    }
  )

  return fetchState
}
